// Public notice board: a scrolling "NOTICE" bar (welcome page) + admin manager.
// Stored in Firestore "announcements" — public read, admin write (see firestore.rules).
import React, { useEffect, useState } from "react";
import { db } from "./firebase";
import { collection, getDocs, addDoc, deleteDoc, updateDoc, doc } from "firebase/firestore";

const today = () => new Date().toISOString().slice(0, 10);
const isNew = n => n.createdAt && Date.now() - n.createdAt < 3 * 24 * 3600 * 1000;
const fmt = d => d ? new Date(d + "T00:00:00").toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "";

async function loadNotices() {
  const snap = await getDocs(collection(db, "announcements"));
  const ms = v => (v && typeof v.toMillis === "function") ? v.toMillis() : (typeof v === "number" ? v : 0);
  return snap.docs
    .map(d => { const x = d.data() || {}; return { ...x, id: d.id, text: String(x.text || x.message || x.title || "").trim(), createdAt: ms(x.createdAt), showUntil: typeof x.showUntil === "string" ? x.showUntil : null }; })
    .filter(n => n.text)
    .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
}

function NoticeBarInner() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    loadNotices().then(list => setItems(list.filter(n => !n.showUntil || n.showUntil >= today()))).catch(() => {});
  }, []);
  if (items.length === 0) return null;
  const secs = Math.max(18, items.reduce((s, n) => s + (n.text || "").length, 0) * 0.28);
  return (
    <div className="notice-bar" role="marquee" aria-label="Notices">
      <div className="notice-tag">📢 NOTICE</div>
      <div className="notice-track">
        <div className="notice-run" style={{ animationDuration: `${secs}s` }}>
          {[0, 1].map(copy => (
            <span key={copy} className="notice-copy" aria-hidden={copy === 1}>
              {items.map((n, i) => (
                <React.Fragment key={n.id}>
                  {i > 0 && <span className="notice-sep">◆</span>}
                  <span className="notice-item">{isNew(n) && <span className="notice-new">NEW</span>}{n.text}</span>
                </React.Fragment>
              ))}
              <span className="notice-sep">◆</span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

export function NoticeManager() {
  const [list, setList] = useState([]);
  const [text, setText] = useState("");
  const [until, setUntil] = useState("");
  const [busy, setBusy] = useState(false);
  const refresh = () => loadNotices().then(setList).catch(e => alert("Could not load notices: " + e.message));
  useEffect(() => { refresh(); }, []);
  const add = async () => {
    if (!text.trim()) return alert("Type the notice first.");
    setBusy(true);
    try { await addDoc(collection(db, "announcements"), { text: text.trim(), showUntil: until || null, createdAt: Date.now() }); setText(""); setUntil(""); refresh(); }
    catch (e) { alert("Error: " + e.message); }
    setBusy(false);
  };
  const remove = async n => { if (!window.confirm(`Delete notice "${n.text}"?`)) return; await deleteDoc(doc(db, "announcements", n.id)); refresh(); };
  const active = n => !n.showUntil || n.showUntil >= today();
  const [editId, setEditId] = useState(null);
  const [eText, setEText] = useState("");
  const [eUntil, setEUntil] = useState("");
  const startEdit = n => { setEditId(n.id); setEText(n.text); setEUntil(n.showUntil || ""); };
  const saveEdit = async () => {
    if (!eText.trim()) return alert("Notice text can't be empty.");
    try { await updateDoc(doc(db, "announcements", editId), { text: eText.trim(), showUntil: eUntil || null }); setEditId(null); refresh(); }
    catch (e) { alert("Error: " + e.message); }
  };
  return (
    <div className="users-section">
      <div className="users-header"><h3>📣 Notices (welcome page)</h3><span className="user-count">{list.filter(active).length} showing</span></div>
      <div className="notice-form">
        <input value={text} onChange={e => setText(e.target.value)} placeholder="e.g. E1 CE C1 & C2 Physics Test on 28/09/2026" onKeyDown={e => e.key === "Enter" && add()} />
        <label>Show until <input type="date" value={until} onChange={e => setUntil(e.target.value)} /></label>
        <button onClick={add} disabled={busy}>{busy ? "Adding…" : "➕ Add notice"}</button>
      </div>
      <p className="notice-hint">Notices scroll on the welcome page for everyone. A notice disappears automatically after its "show until" date; leave the date blank to keep it until you delete it. Notices added in the last 3 days show a blinking <b>NEW</b> tag.</p>
      {list.length === 0 ? <p style={{ color: "rgba(255,255,255,0.5)" }}>No notices yet.</p> : (
        <ul className="notice-list">
          {list.map(n => (
            editId === n.id ? (
              <li key={n.id} className="notice-editing">
                <input value={eText} onChange={e => setEText(e.target.value)} autoFocus onKeyDown={e => { if (e.key === "Enter") saveEdit(); if (e.key === "Escape") setEditId(null); }} />
                <input type="date" value={eUntil} onChange={e => setEUntil(e.target.value)} title="Show until (leave blank for no end date)" />
                <button className="notice-save" onClick={saveEdit}>💾 Save</button>
                <button className="notice-cancel" onClick={() => setEditId(null)}>Cancel</button>
              </li>
            ) : (
            <li key={n.id} className={active(n) ? "" : "expired"}>
              <span>{isNew(n) && active(n) && <span className="notice-new">NEW</span>}{n.text}</span>
              <span className="notice-meta">{n.showUntil ? (active(n) ? `until ${fmt(n.showUntil)}` : `expired ${fmt(n.showUntil)}`) : "no end date"}</span>
              <button onClick={() => startEdit(n)} title="Edit">✏️</button>
              <button onClick={() => remove(n)} title="Delete">🗑</button>
            </li>
            )
          ))}
        </ul>
      )}
    </div>
  );
}

// Safety net: if a notice ever breaks, hide the bar instead of blanking the page.
class NoticeGuard extends React.Component {
  constructor(p) { super(p); this.state = { failed: false }; }
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(e) { console.warn("NoticeBar hidden:", e); }
  render() { return this.state.failed ? null : this.props.children; }
}
export function NoticeBar() { return <NoticeGuard><NoticeBarInner /></NoticeGuard>; }
